import { LOGOS as ICONS } from "@/lib/logos";
import { cn } from "@/lib/utils";

// Client logos from Simple Icons (CC0 paths, bundled; src/lib/logos.ts). They
// only name the client. DESIGN.md: Client logos — muted at rest, the brand
// color on hover and when selected, and on-surface when the brand color has
// less than 3:1 contrast with the page.

// DESIGN.md colors.surface. Brand colors are checked against it once.
const SURFACE = "#1C1C1C";

function luminance(hex: string): number {
  const n = parseInt(hex.replace("#", ""), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}

/** The brand color for a client, or on-surface when it would be unreadable. */
export function brandColor(id: string): string {
  const icon = ICONS[id];
  if (!icon) return "var(--zsd-colors-on-surface)";
  const hex = `#${icon.hex}`;
  return contrast(hex, SURFACE) >= 3 ? hex : "var(--zsd-colors-on-surface)";
}

/**
 * A client logo in currentColor. Set `--brand` with brandColor() on an
 * ancestor and add the `brand` class to it to switch color on hover or
 * selection (globals.css).
 */
/** URL of the client's logo file in public/logos (scripts/render-logos.ts). */
export function logoUrl(id: string): string | null {
  const icon = ICONS[id];
  return icon ? `/logos/${icon.slug}.svg` : null;
}

export function BrandLogo({ id, size = 20, className }: { id: string; size?: number; className?: string }) {
  const icon = ICONS[id];
  if (!icon) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="currentColor"
      className={cn("shrink-0", className)}
    >
      <path d={icon.path} />
    </svg>
  );
}
